import { useState, useEffect, useRef } from "react";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { format } from "date-fns";

interface CreateRoutineTaskModalProps {
  onClose: () => void;
  defaultProjectId?: string;
}

export default function CreateRoutineTaskModal({ onClose, defaultProjectId }: CreateRoutineTaskModalProps) {
  const { projects, users, addRoutineTask, currentUser } = useApp();
  const { t } = useLanguage();
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    projectId: defaultProjectId || projects[0]?.id || "",
    name: "",
    description: "",
    repeatType: "weekly" as "none" | "daily" | "weekly" | "monthly" | "quarterly" | "semi-annually" | "yearly",
    initialDueDate: format(new Date(), 'yyyy-MM-dd'),
    assigneeIds: [] as string[],
    useMonthEnd: false,
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.projectId || !currentUser) return;
    if (!formData.useMonthEnd && !formData.initialDueDate) return;

    addRoutineTask({
      projectId: formData.projectId,
      name: formData.name,
      description: formData.description,
      repeatPattern: {
        type: formData.repeatType,
        initialDueDate: formData.useMonthEnd ? undefined : new Date(formData.initialDueDate),
        useMonthEnd: formData.useMonthEnd,
      },
      assigneeIds: formData.assigneeIds,
      isActive: true,
      creatorId: currentUser.id,
    });

    onClose();
  };

  const showMonthEndOption = ["monthly", "quarterly", "semi-annually", "yearly"].includes(formData.repeatType);

  const getFrequencyText = () => {
    switch (formData.repeatType) {
      case "daily": return t('tasks.everyDay');
      case "weekly": return t('tasks.everyWeek');
      case "monthly": return t('tasks.everyMonth');
      case "quarterly": return t('tasks.every3Months');
      case "semi-annually": return t('tasks.every6Months');
      case "yearly": return t('tasks.everyYear');
      case "none": return t('tasks.onceOnly');
      default: return "";
    }
  };

  // Scroll to top when modal opens
  useEffect(() => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent ref={dialogContentRef} className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('tasks.createTask')}</DialogTitle>
          <DialogDescription>
            {t('tasks.completeTriggerDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Info Section */}
          <div className="space-y-4">
            <div className="pb-2 border-b">
              <h3 className="text-gray-900">{t('tasks.basicInfo')}</h3>
              <p className="text-sm text-gray-600">{t('tasks.basicInfoDesc')}</p>
            </div>

            {/* Project Selection */}
            <div>
              <Label className="mb-2" htmlFor="project">{t('projects.project')} *</Label>
              <select
                id="project"
                value={formData.projectId}
                onChange={(e) =>
                  setFormData({ ...formData, projectId: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Task Name */}
            <div>
              <Label className="mb-2" htmlFor="name">{t('tasks.taskName')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('tasks.taskExample')}
              />
            </div>

            {/* Description */}
            <div>
              <Label className="mb-2" htmlFor="description">{t('tasks.taskSummary')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={t('tasks.taskDescPlaceholder')}
                rows={3}
              />
            </div>

            {/* Assignee Selection */}
            <div>
              <Label className="mb-2 block">{t('tasks.assigneesMultiple')}</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`assignee-${user.id}`}
                      checked={formData.assigneeIds.includes(user.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({
                            ...formData,
                            assigneeIds: [...formData.assigneeIds, user.id],
                          });
                        } else {
                          setFormData({
                            ...formData,
                            assigneeIds: formData.assigneeIds.filter(
                              (id) => id !== user.id
                            ),
                          });
                        }
                      }}
                    />
                    <label
                      htmlFor={`assignee-${user.id}`}
                      className="text-gray-700 cursor-pointer flex items-center gap-2"
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs">
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <div>{user.name}</div>
                        <div className="text-xs text-gray-600">{user.email}</div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Due Date Management Section */}
          <div className="space-y-4">
            <div className="pb-2 border-b">
              <h3 className="text-gray-900">{t('tasks.dueDateManagement')}</h3>
              <p className="text-sm text-gray-600">{t('tasks.dueDateManagementDesc')}</p>
            </div>

            {/* Repeat Pattern */}
            <div>
              <Label className="mb-2" htmlFor="repeat-type">{t('tasks.frequency')} *</Label>
              <select
                id="repeat-type"
                value={formData.repeatType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    repeatType: e.target.value as any,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="none">{t('tasks.none')}</option>
                <option value="daily">{t('tasks.daily')}</option>
                <option value="weekly">{t('tasks.weekly')}</option>
                <option value="monthly">{t('tasks.monthly')}</option>
                <option value="quarterly">{t('tasks.quarterly')}</option>
                <option value="semi-annually">{t('tasks.semiAnnually')}</option>
                <option value="yearly">{t('tasks.yearly')}</option>
              </select>
            </div>

            {/* Initial Due Date */}
            <div>
              <Label className="mb-2" htmlFor="initial-due-date">{t('tasks.initialDueDate')} *</Label>
              <Input
                id="initial-due-date"
                type="date"
                value={formData.initialDueDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    initialDueDate: e.target.value,
                  })
                }
                disabled={formData.useMonthEnd}
              />
              {!formData.useMonthEnd && (
                <p className="text-xs text-gray-600 mt-1">
                  💡 {getFrequencyText()} {t('tasks.taskAutoGenerate')}
                </p>
              )}
            </div>

            {/* Month End Checkbox */}
            {showMonthEndOption && (
              <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                <Checkbox
                  id="use-month-end"
                  checked={formData.useMonthEnd}
                  onCheckedChange={(checked) => {
                    setFormData({
                      ...formData,
                      useMonthEnd: !!checked,
                    });
                  }}
                />
                <label
                  htmlFor="use-month-end"
                  className="text-sm text-gray-700 cursor-pointer"
                >
                  {t('tasks.useMonthEndNoDate')}
                </label>
              </div>
            )}
          </div>

          {/* Completion Trigger Info */}
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-blue-900 mb-1">💡 {t('tasks.completeTriggerTitle')}</p>
            <p className="text-blue-700 text-sm">
              {t('tasks.completeTriggerDesc')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!formData.name || !formData.projectId || (!formData.useMonthEnd && !formData.initialDueDate)}
          >
            {t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
